export type PerformanceScoreDisplayLabel = 'Outstanding' | 'Very Good' | 'Good' | 'Fair' | 'Low';

export interface PerformanceScoreDisplay {
  label: PerformanceScoreDisplayLabel;
  text: string;
}

const TOP_THRESHOLD_COPY: Record<Exclude<PerformanceScoreDisplayLabel, 'Fair' | 'Low'>, string> = {
  Outstanding: 'Top 10%',
  'Very Good': 'Top 20%',
  Good: 'Top 30%',
};

const normalizeLabel = (label: string | null | undefined): PerformanceScoreDisplayLabel | null => {
  switch ((label || '').trim().toLowerCase()) {
    case 'outstanding':
      return 'Outstanding';
    case 'very good':
    case 'great':
      return 'Very Good';
    case 'good':
      return 'Good';
    case 'fair':
      return 'Fair';
    case 'low':
      return 'Low';
    default:
      return null;
  }
};

// Fallback bands mirror the API's getPercentileLabel (percentileLabel.ts):
// >=90 Outstanding, >=80 Great/Very Good, >=70 Good, >=30 Fair, <30 Low.
const labelFromPercentile = (percentile: number | null | undefined): PerformanceScoreDisplayLabel | null => {
  if (percentile == null || !Number.isFinite(percentile)) return null;
  if (percentile >= 90) return 'Outstanding';
  if (percentile >= 80) return 'Very Good';
  if (percentile >= 70) return 'Good';
  if (percentile >= 30) return 'Fair';
  return 'Low';
};

export const getPerformanceScoreDisplay = (
  label: string | null | undefined,
  percentile: number | null | undefined,
): PerformanceScoreDisplay | null => {
  // The API-provided label is the source of truth; the percentile is only a
  // fallback for payloads that carry a percentile without a label.
  const displayLabel = normalizeLabel(label) || labelFromPercentile(percentile);
  if (!displayLabel) return null;

  if (displayLabel === 'Fair' || displayLabel === 'Low') {
    return { label: displayLabel, text: displayLabel };
  }

  return {
    label: displayLabel,
    text: `${displayLabel} (${TOP_THRESHOLD_COPY[displayLabel]})`,
  };
};
